import { unstable_batchedUpdates } from 'react-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { format, parseISO } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  TaskName,
  TerminalStatusLogOperation,
  TerminalRouteStatus,
  type TerminalStatusLogEntry as GeneratedTerminalStatusLogEntry,
  useTerminalStatusTruckFillAndTimeUpdateSubscription,
  Maybe,
  useTerminalStatusTftUpdateMutation,
  useUpdateCarTrailerDetailsMutation,
} from '../../generated/graphql';
import { useUserConfiguration } from '../../providers/UserConfigurationProvider';
import {
  TERMINAL_STATUS_OPTIONS_WITH_ICONS,
  isTerminalRouteStatus,
} from '../../utils/terminalStatusOptions';

type TerminalStatusLogEntry = Pick<
  GeneratedTerminalStatusLogEntry,
  | 'id'
  | 'status'
  | 'timestamp'
  | 'timeZone'
  | 'legId'
  | 'locationId'
  | 'locationOrder'
  | 'locationLabel'
  | 'locationName'
  | 'createdBy'
  | 'updatedBy'
  | 'updatedAt'
  | 'gate'
>;

function normalizeTerminalStatusLogEntries(
  terminalStatusLog?: Maybe<
    Array<Maybe<GeneratedTerminalStatusLogEntry>>
  > | null,
): TerminalStatusLogEntry[] {
  return (terminalStatusLog ?? [])
    .filter((log): log is GeneratedTerminalStatusLogEntry => log != null)
    .map((log) => ({
      id: log.id,
      status: log.status,
      timestamp: log.timestamp,
      timeZone: log.timeZone,
      legId: log.legId,
      locationId: log.locationId,
      locationOrder: log.locationOrder,
      locationLabel: log.locationLabel,
      locationName: log.locationName,
      createdBy: log.createdBy,
      updatedBy: log.updatedBy,
      updatedAt: log.updatedAt,
      gate: log.gate,
    }));
}

interface TerminalStatusRouteLocation {
  id?: number;
  position?: number | null;
  deletedAt: Maybe<string>;
  location?: {
    id: number;
    name: string;
    city: string;
    country: string;
  } | null;
}

interface TerminalStatusModalRow {
  legId: number;
  routeId: string;
  containerId?: string | null;
  position?: number | null;
  terminalStatus?: TerminalRouteStatus | null;
  terminalStatusDescription?: string | null;
  terminalStatusUpdatedAt?: string | null;
  terminalStatusLog?: TerminalStatusLogEntry[] | null;
  carGateNumber?: string | null;
  carDetailId?: number;
  trailerGateNumber?: string | null;
  transportationDate?: string | null;
  location?: {
    id: number;
    name: string;
    city: string;
    country: string;
  } | null;
  route?: {
    legs?: TerminalStatusRouteLocation[] | null;
  } | null;
}

export interface TerminalStatusModalUpdate {
  legId: number;
  routeId: string;
  actualArrivalDate?: string | null;
  actualArrivalTime?: string | null;
  actualDepartureDate?: string | null;
  actualDepartureTime?: string | null;
  actualDockDate?: string | null;
  actualDockTime?: string | null;
  actualUndockDate?: string | null;
  actualUndockTime?: string | null;
  terminalStatus?: TerminalRouteStatus | null;
  terminalStatusDescription?: string | null;
  terminalStatusUpdatedAt?: string | null;
  terminalStatusLog?: TerminalStatusLogEntry[] | null;
  updatedAt?: string | null;
  lastUpdatedBy?: string | null;
  carGateNumber?: string | null;
}

interface TerminalStatusModalProps {
  row: TerminalStatusModalRow;
  locationId: number;
  onError: (message: string) => void;
  onStatusUpdated?: (updatedRow: TerminalStatusModalUpdate) => void;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const TERMINAL_STATUS_SETTINGS_TABLE = 'terminalStatusSettings';
const TERMINAL_STATUS_SETTINGS_KEY = 'selectedStatuses';

function getStatusIconSx(status?: TerminalRouteStatus | null) {
  if (status === TerminalRouteStatus.Pickup) {
    return { transform: 'rotate(-90deg)' };
  }

  if (status === TerminalRouteStatus.HandedOver) {
    return { transform: 'rotate(90deg)' };
  }

  return undefined;
}

function createStatusIconElement(
  IconComponent: typeof BlockOutlinedIcon,
  status?: TerminalRouteStatus | null,
) {
  return <IconComponent fontSize="small" sx={getStatusIconSx(status)} />;
}

function formatTimestamp(timestamp?: string | null) {
  if (!timestamp) {
    return '-';
  }

  const parsed = parseISO(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return timestamp;
  }

  return format(parsed, 'yyyy-MM-dd HH:mm');
}

function formatTimestampInput(timestamp?: string | null) {
  if (!timestamp) {
    return '';
  }

  const parsed = parseISO(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return format(parsed, "yyyy-MM-dd'T'HH:mm");
}

function getLocationText(
  locationName?: string | null,
  locationCity?: string | null,
) {
  if (locationName && locationCity) {
    return `${locationName} · ${locationCity}`;
  }

  if (locationName) {
    return locationName;
  }

  if (locationCity) {
    return locationCity;
  }

  return '-';
}

export function TerminalStatusModal(props: TerminalStatusModalProps) {
  const { row, locationId, onError, onStatusUpdated, isOpen, onOpen, onClose } =
    props;
  const { getCustomConfiguration } = useUserConfiguration();
  const [internalOpen, setInternalOpen] = useState(false);
  const [deleteLog, setDeleteLog] = useState<TerminalStatusLogEntry | null>(
    null,
  );
  const [isDeletingLog, setIsDeletingLog] = useState(false);
  const [editingLogId, setEditingLogId] = useState<string | null>(null);
  const [editingTimestamp, setEditingTimestamp] = useState('');
  const [logExpanded, setLogExpanded] = useState(false);
  const [selectedStatus, setSelectedStatus] =
    useState<TerminalRouteStatus | null>(null);
  const [highlightedStatus, setHighlightedStatus] =
    useState<TerminalRouteStatus | null>(null);
  // Spinner shown inside the clicked status button while the mutation runs.
  const [pendingStatus, setPendingStatus] =
    useState<TerminalRouteStatus | null>(null);
  const [gateDialogOpen, setGateDialogOpen] = useState(false);
  const [pendingGateNumber, setPendingGateNumber] = useState('');
  const [updateCarTrailerDetails] = useUpdateCarTrailerDetailsMutation();
  const [updateTerminalStatus, { loading }] =
    useTerminalStatusTftUpdateMutation();
  const open = isOpen ?? internalOpen;
  // Skip incoming subscription updates while the user is mid-action.
  const isBusyRef = useRef(false);
  isBusyRef.current =
    editingLogId !== null ||
    deleteLog !== null ||
    isDeletingLog ||
    gateDialogOpen;
  // Suppress only the exact echo (same updatedAt) from our own mutation.
  const suppressUpdatedAtRef = useRef<string | null>(null);

  // Live updates while the modal is open; Apollo unsubscribes when skip flips.
  useTerminalStatusTruckFillAndTimeUpdateSubscription({
    variables: { locationId: locationId },
    skip: !open || locationId === 0 || !row.legId,
    onSubscriptionData({ subscriptionData }) {
      const payload = subscriptionData.data?.truckFillAndTimeUpdate;
      if (!payload || payload.refreshWholeGrid !== false) return;

      const subRouteLeg = payload.routeLeg;
      if (!subRouteLeg || subRouteLeg.id !== row.legId) return;

      if (isBusyRef.current) return;

      if (
        suppressUpdatedAtRef.current != null &&
        subRouteLeg.updatedAt != null &&
        subRouteLeg.updatedAt === suppressUpdatedAtRef.current
      ) {
        suppressUpdatedAtRef.current = null;
        return;
      }

      // Push updates to the parent so the row re-renders with fresh props.
      onStatusUpdated?.({
        legId: subRouteLeg.id,
        routeId: row.routeId,
        actualArrivalDate: subRouteLeg.actualArrivalDate ?? null,
        actualArrivalTime: subRouteLeg.actualArrivalTime ?? null,
        actualDepartureDate: subRouteLeg.actualDepartureDate ?? null,
        actualDepartureTime: subRouteLeg.actualDepartureTime ?? null,
        actualDockDate: subRouteLeg.actualDockDate ?? null,
        actualDockTime: subRouteLeg.actualDockTime ?? null,
        actualUndockDate: subRouteLeg.actualUndockDate ?? null,
        actualUndockTime: subRouteLeg.actualUndockTime ?? null,
        terminalStatus: subRouteLeg.terminalStatus ?? null,
        terminalStatusDescription:
          subRouteLeg.terminalStatusDescription ?? null,
        terminalStatusUpdatedAt: subRouteLeg.terminalStatusUpdatedAt ?? null,
        terminalStatusLog: (subRouteLeg.terminalStatusLog ?? []).filter(
          (log): log is TerminalStatusLogEntry => log != null,
        ),
        updatedAt: subRouteLeg.updatedAt ?? null,
        lastUpdatedBy: subRouteLeg.lastUpdatedBy ?? null,
      });
    },
  });

  const configuredStatuses = getCustomConfiguration(
    TERMINAL_STATUS_SETTINGS_TABLE,
    TERMINAL_STATUS_SETTINGS_KEY,
  );

  const handleOpen = () => {
    if (isOpen === undefined) {
      setInternalOpen(true);
    }

    onOpen?.();
  };

  const handleClose = () => {
    if (isOpen === undefined) {
      setInternalOpen(false);
    }

    onClose?.();
  };

  const enabledStatuses = useMemo(() => {
    if (!Array.isArray(configuredStatuses)) {
      return TERMINAL_STATUS_OPTIONS_WITH_ICONS.map((option) => option.status);
    }

    const values = configuredStatuses.filter(isTerminalRouteStatus);
    return values.length > 0
      ? values
      : TERMINAL_STATUS_OPTIONS_WITH_ICONS.map((option) => option.status);
  }, [configuredStatuses]);

  const availableStatusOptions = useMemo(
    () =>
      TERMINAL_STATUS_OPTIONS_WITH_ICONS.filter(
        (option) =>
          enabledStatuses.includes(option.status) &&
          option.status !== TerminalRouteStatus.UnplannedRoute,
      ),
    [enabledStatuses],
  );

  const statusLog = useMemo(
    () =>
      [...(row.terminalStatusLog ?? [])].sort(
        (left, right) =>
          new Date(right.timestamp).getTime() -
          new Date(left.timestamp).getTime(),
      ),
    [row.terminalStatusLog],
  );

  const statusLogMatrix = useMemo(() => {
    const matrix = new Map<string, TerminalStatusLogEntry>();

    statusLog.forEach((log) => {
      const key = `${log.locationOrder}-${log.status}`;

      if (!matrix.has(key)) {
        matrix.set(key, log);
      }
    });

    return matrix;
  }, [statusLog]);

  const routeLocationRows = useMemo(() => {
    const locations: Record<
      number,
      {
        legId: number | null;
        locationOrder: number;
        locationName: string;
        locationCity: string;
      }
    > = {};

    (row.route?.legs ?? [])
      .slice()
      .filter((leg) => leg.deletedAt == null) // Exclude deleted legs
      .sort(
        (left, right) =>
          (left.position ?? Number.MAX_SAFE_INTEGER) -
          (right.position ?? Number.MAX_SAFE_INTEGER),
      )
      .forEach((leg, index) => {
        const fallbackOrder = index + 1;
        const locationOrder =
          leg.position != null && leg.position >= 0
            ? leg.position + 1
            : fallbackOrder;

        locations[locationOrder] = {
          legId: leg.id ?? null,
          locationOrder,
          locationName: leg.location?.name ?? '-',
          locationCity: leg.location?.city ?? '',
        };
      });

    if (!Object.keys(locations).length) {
      const fallbackOrder =
        row.position != null && row.position >= 0 ? row.position + 1 : 1;

      locations[fallbackOrder] = {
        legId: row.legId,
        locationOrder: fallbackOrder,
        locationName: row.location?.name ?? '-',
        locationCity: row.location?.city ?? '',
      };
    }

    return Object.values(locations).sort(
      (left, right) => left.locationOrder - right.locationOrder,
    );
  }, [
    row.route?.legs,
    row.legId,
    row.position,
    row.location?.name,
    row.location?.city,
  ]);

  const locationRows = useMemo(() => {
    const locations = [...routeLocationRows];

    statusLog.forEach((log) => {
      if (
        !locations.some(
          (location) => location.locationOrder === log.locationOrder,
        )
      ) {
        locations.push({
          legId: log.legId,
          locationOrder: log.locationOrder,
          locationName: log.locationName || '-',
          locationCity: '',
        });
      }
    });

    return locations.sort(
      (left, right) => left.locationOrder - right.locationOrder,
    );
  }, [routeLocationRows, statusLog]);

  const activeLocation = useMemo(
    () =>
      locationRows.find((location) => location.legId === row.legId) ??
      locationRows.find(
        (location) => location.locationOrder === (row.position ?? 0) + 1,
      ) ??
      locationRows[0] ??
      null,
    [locationRows, row.legId, row.position],
  );

  const editingLog = useMemo(
    () => statusLog.find((log) => log.id === editingLogId) ?? null,
    [editingLogId, statusLog],
  );

  const activeLocationStatuses = useMemo(() => {
    if (!activeLocation) {
      return new Set<TerminalRouteStatus>();
    }

    return new Set(
      statusLog
        .filter((log) => {
          if (activeLocation.legId != null) {
            return log.legId === activeLocation.legId;
          }

          return log.locationOrder === activeLocation.locationOrder;
        })
        .map((log) => log.status),
    );
  }, [activeLocation, statusLog]);

  useEffect(() => {
    if (!open) {
      return;
    }

    unstable_batchedUpdates(() => {
      setDeleteLog(null);
      setIsDeletingLog(false);
      setEditingLogId(null);
      setEditingTimestamp('');
      setLogExpanded(false);
      setSelectedStatus(null);
      setHighlightedStatus(null);
      setPendingStatus(null);
      setGateDialogOpen(false);
      setPendingGateNumber('');
    });
  }, [enabledStatuses, open, row.legId]);

  const visibleStatus = selectedStatus ?? highlightedStatus;

  const currentSelectionText =
    TERMINAL_STATUS_OPTIONS_WITH_ICONS.find(
      (option) => option.status === visibleStatus,
    )?.label ?? 'No status selected';

  const selectedStatusSummary = `${
    editingLogId ? 'Editing timestamp' : 'Selected'
  } status | ${currentSelectionText}${
    activeLocation
      ? ` · ${getLocationText(
          activeLocation.locationName,
          activeLocation.locationCity,
        )}`
      : ''
  }`;

  const handleStatusToggle = (
    _event: React.MouseEvent<HTMLElement>,
    nextStatus: TerminalRouteStatus | null,
  ) => {
    if (nextStatus === null || loading) {
      return;
    }

    setSelectedStatus(nextStatus);
    setHighlightedStatus(nextStatus);

    if (nextStatus === TerminalRouteStatus.HandedOver) {
      setPendingGateNumber(row.carGateNumber ?? '');
      setGateDialogOpen(true);
      return;
    }

    setPendingStatus(nextStatus);
    void submitMutation(nextStatus);
  };

  const handleGateDialogConfirm = () => {
    setGateDialogOpen(false);
    setPendingStatus(TerminalRouteStatus.HandedOver);
    void submitMutation(TerminalRouteStatus.HandedOver, pendingGateNumber);
  };

  const handleGateDialogCancel = () => {
    setGateDialogOpen(false);
    setPendingGateNumber('');
    setSelectedStatus(null);
    setHighlightedStatus(
      row.terminalStatus && enabledStatuses.includes(row.terminalStatus)
        ? row.terminalStatus
        : null,
    );
  };

  const submitMutation = async (
    statusToSave?: TerminalRouteStatus | null,
    gateNumber?: string,
  ) => {
    const isEditing = editingLogId != null;
    const appliedStatus = isEditing
      ? editingLog?.status ?? null
      : statusToSave ?? selectedStatus;

    if (!isEditing && !appliedStatus) {
      onError('Select a status before saving.');
      return;
    }

    if (isEditing && !editingLog) {
      onError('Selected log entry was not found.');
      return;
    }

    if (isEditing && !editingTimestamp) {
      onError('Select a timestamp before saving the edit.');
      return;
    }

    const nextTimestamp = isEditing ? new Date(editingTimestamp) : null;
    if (
      isEditing &&
      (!nextTimestamp || Number.isNaN(nextTimestamp.getTime()))
    ) {
      onError('Invalid timestamp for terminal status edit.');
      return;
    }

    try {
      // Run gate update first and await it so the carDetails row is fully
      // persisted before updateTruckFillAndTime loads the leg.  Running both
      // in parallel caused a race where updateTruckFillAndTime would read the
      // stale carDetails (gateNumber = null) and then cascade-save it over the
      // value just written by updateCarTrailerDetails.
      if (gateNumber !== undefined) {
        await updateCarTrailerDetails({
          variables: {
            input: {
              legId: row.legId,
              ...(row.carDetailId ? { id: row.carDetailId } : {}),
              type: TaskName.Car,
              gateNumber,
            },
          },
        });
      }

      const response = await updateTerminalStatus({
        variables: {
          truckFillAndTimeInput: {
            id: isEditing ? editingLog!.legId : row.legId,
            locationId,
            terminalStatusInput: {
              operation: TerminalStatusLogOperation.Upsert,
              logId: editingLogId,
              status: isEditing ? editingLog!.status : appliedStatus,
              timestamp: isEditing ? nextTimestamp!.toISOString() : undefined,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              ...(gateNumber !== undefined ? { gate: gateNumber || null } : {}),
            },
          },
        },
      });

      const updatedLeg = response.data?.truckFillAndTime;

      if (updatedLeg) {
        suppressUpdatedAtRef.current = updatedLeg.updatedAt ?? null;
        onStatusUpdated?.({
          legId: updatedLeg.id,
          routeId: row.routeId,
          actualArrivalDate: updatedLeg.actualArrivalDate,
          actualArrivalTime: updatedLeg.actualArrivalTime,
          actualDepartureDate: updatedLeg.actualDepartureDate,
          actualDepartureTime: updatedLeg.actualDepartureTime,
          actualDockDate: updatedLeg.actualDockDate,
          actualDockTime: updatedLeg.actualDockTime,
          actualUndockDate: updatedLeg.actualUndockDate,
          actualUndockTime: updatedLeg.actualUndockTime,
          terminalStatus: updatedLeg.terminalStatus,
          terminalStatusDescription: updatedLeg.terminalStatusDescription,
          terminalStatusUpdatedAt: updatedLeg.terminalStatusUpdatedAt,
          terminalStatusLog: normalizeTerminalStatusLogEntries(
            updatedLeg.terminalStatusLog,
          ),
          updatedAt: updatedLeg.updatedAt,
          lastUpdatedBy: updatedLeg.lastUpdatedBy,
          ...(gateNumber !== undefined ? { carGateNumber: gateNumber } : {}),
        });
      }

      unstable_batchedUpdates(() => {
        setEditingLogId(null);
        setEditingTimestamp('');
        setSelectedStatus(null);
        setHighlightedStatus(appliedStatus);
      });
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : 'Terminal status update failed.',
      );
    } finally {
      setPendingStatus(null);
    }
  };

  const commitEditedTimestamp = () => {
    if (!editingLogId || !editingLog || loading) {
      return;
    }

    const originalTimestamp = formatTimestampInput(editingLog.timestamp);
    if (!editingTimestamp || editingTimestamp === originalTimestamp) {
      return;
    }

    void submitMutation();
  };

  const handleCancelEdit = () => {
    setEditingLogId(null);
    setEditingTimestamp('');
    setSelectedStatus(null);
    setHighlightedStatus(
      row.terminalStatus && enabledStatuses.includes(row.terminalStatus)
        ? row.terminalStatus
        : null,
    );
  };

  const handleEditLog = (log: TerminalStatusLogEntry) => {
    if (!enabledStatuses.includes(log.status)) {
      onError('Enable this status in Terminal Status Settings before editing.');
      return;
    }

    setEditingLogId(log.id);
    setSelectedStatus(log.status);
    setHighlightedStatus(log.status);
    setEditingTimestamp(formatTimestampInput(log.timestamp));
  };

  const handleDeleteLog = async () => {
    if (!deleteLog || isDeletingLog) {
      return;
    }

    try {
      setIsDeletingLog(true);

      const response = await updateTerminalStatus({
        variables: {
          truckFillAndTimeInput: {
            id: deleteLog.legId,
            locationId,
            terminalStatusInput: {
              operation: TerminalStatusLogOperation.Delete,
              logId: deleteLog.id,
            },
          },
        },
      });

      const updatedLeg = response.data?.truckFillAndTime;

      if (updatedLeg) {
        suppressUpdatedAtRef.current = updatedLeg.updatedAt ?? null;
        onStatusUpdated?.({
          legId: updatedLeg.id,
          routeId: row.routeId,
          actualArrivalDate: updatedLeg.actualArrivalDate,
          actualArrivalTime: updatedLeg.actualArrivalTime,
          actualDepartureDate: updatedLeg.actualDepartureDate,
          actualDepartureTime: updatedLeg.actualDepartureTime,
          actualDockDate: updatedLeg.actualDockDate,
          actualDockTime: updatedLeg.actualDockTime,
          actualUndockDate: updatedLeg.actualUndockDate,
          actualUndockTime: updatedLeg.actualUndockTime,
          terminalStatus: updatedLeg.terminalStatus,
          terminalStatusDescription: updatedLeg.terminalStatusDescription,
          terminalStatusUpdatedAt: updatedLeg.terminalStatusUpdatedAt,
          terminalStatusLog: normalizeTerminalStatusLogEntries(
            updatedLeg.terminalStatusLog,
          ),
          updatedAt: updatedLeg.updatedAt,
          lastUpdatedBy: updatedLeg.lastUpdatedBy,
        });
      }

      // Reset the highlighted toggle to match the server's current status so
      // the user can re-click the same status button (e.g. HandedOver) without
      // MUI exclusive-toggle firing null (deselect) instead of the status value.
      const newStatus = (updatedLeg?.terminalStatus ??
        null) as TerminalRouteStatus | null;
      unstable_batchedUpdates(() => {
        setHighlightedStatus(
          newStatus && enabledStatuses.includes(newStatus) ? newStatus : null,
        );
        setSelectedStatus(null);
        if (editingLogId === deleteLog.id) {
          setEditingLogId(null);
          setEditingTimestamp('');
        }
        setDeleteLog(null);
      });
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : 'Terminal status delete failed.',
      );
    } finally {
      setIsDeletingLog(false);
    }
  };

  const iconForCurrentStatus = TERMINAL_STATUS_OPTIONS_WITH_ICONS.find(
    (option) => option.status === row.terminalStatus,
  ) ?? {
    status: TerminalRouteStatus.Departure as any,
    color: '#9aa4b2',
    icon: RadioButtonUncheckedOutlinedIcon,
    label: 'Status',
  };

  const deleteLogLabel = deleteLog
    ? TERMINAL_STATUS_OPTIONS_WITH_ICONS.find(
        (option) => option.status === deleteLog.status,
      )?.label ?? deleteLog.status
    : 'this status';

  const formattedTransportationDate = useMemo(() => {
    if (!row.transportationDate) {
      return '';
    }

    const parsed = parseISO(row.transportationDate);

    if (Number.isNaN(parsed.getTime())) {
      return row.transportationDate;
    }

    return format(parsed, 'yyyy-MM-dd');
  }, [row.transportationDate]);

  const headerSegments = [
    row.routeId,
    row.containerId?.trim(),
    formattedTransportationDate,
  ].filter((value): value is string => Boolean(value));

  const canSaveEditedTimestamp = useMemo(() => {
    if (!editingLogId || !editingLog || loading) {
      return false;
    }

    const originalTimestamp = formatTimestampInput(editingLog.timestamp);
    return Boolean(editingTimestamp) && editingTimestamp !== originalTimestamp;
  }, [editingLog, editingLogId, editingTimestamp, loading]);

  return (
    <>
      <Tooltip title={row.terminalStatusDescription ?? 'Update route status'}>
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            borderRadius: 1,
            border: `1px solid ${iconForCurrentStatus.color}`,
            color: iconForCurrentStatus.color,
          }}
        >
          {createStatusIconElement(
            iconForCurrentStatus.icon,
            iconForCurrentStatus.status,
          )}
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        keepMounted
        maxWidth={false}
        PaperProps={{ sx: { maxWidth: '1500px' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            py: 1.5,
            px: 3,
            my: 0.25,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Box>
            <Typography
              component="div"
              sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 0.25 }}
            >
              {headerSegments.join(' | ')}
              {' | '}
              <strong style={{ color: '#000', fontWeight: 700 }}>
                EDIT TERMINAL STATUS
              </strong>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
            <IconButton
              onClick={handleClose}
              size="small"
              disabled={loading || isDeletingLog}
              aria-label="Close terminal status modal"
              sx={{
                backgroundColor: '#9e9e9e',
                color: '#fff',
                borderRadius: 1,
                '&:hover': { backgroundColor: '#757575' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <ToggleButtonGroup
              value={visibleStatus}
              exclusive
              onChange={handleStatusToggle}
              aria-label="terminal status selection"
              sx={{
                flexWrap: 'wrap',
                gap: '0.5rem 2.5rem',
                '& .MuiToggleButtonGroup-grouped': {
                  borderRadius: '8px !important',
                  border: '1px solid #d0d7de !important',
                  margin: 0,
                },
              }}
            >
              {availableStatusOptions.map((option) => {
                const alreadyAddedForActiveLocation =
                  editingLogId == null &&
                  activeLocationStatuses.has(option.status);
                const isPending = pendingStatus === option.status;

                return (
                  <ToggleButton
                    key={option.status}
                    value={option.status}
                    aria-label={option.label}
                    disabled={
                      loading ||
                      editingLogId != null ||
                      alreadyAddedForActiveLocation
                    }
                    sx={{
                      minWidth: 125,
                      display: 'flex',
                      gap: 0.75,
                      alignItems: 'center',
                      fontWeight: 700,
                      borderColor: option.color,
                      color: '#fff',
                      backgroundColor: option.color,
                      '&:hover': {
                        backgroundColor: option.color,
                        borderColor: option.color,
                        color: '#fff',
                      },
                      '&.Mui-selected': {
                        backgroundColor: `${option.color} !important`,
                        color: '#fff !important',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: option.color,
                        color: '#fff',
                        opacity: isPending ? 1 : 0.6,
                      },
                    }}
                  >
                    {isPending ? (
                      <CircularProgress
                        size={16}
                        thickness={5}
                        sx={{ color: '#fff' }}
                      />
                    ) : (
                      createStatusIconElement(option.icon, option.status)
                    )}
                    {option.label}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
            {availableStatusOptions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No terminal statuses are enabled. Update them from Terminal
                Status Settings in the user menu.
              </Typography>
            ) : null}
            {editingLog ? (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Edit updates only the timestamp for this status entry.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                >
                  <TextField
                    label="Status timestamp"
                    type="datetime-local"
                    value={editingTimestamp}
                    onChange={(event) =>
                      setEditingTimestamp(event.target.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        commitEditedTimestamp();
                      }
                    }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ maxWidth: 280 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button onClick={handleCancelEdit}>Cancel edit</Button>
                    <Button
                      variant="contained"
                      onClick={commitEditedTimestamp}
                      disabled={!canSaveEditedTimestamp}
                    >
                      Save
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            ) : null}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Chip label={selectedStatusSummary} variant="outlined" />
            </Stack>
            <Divider />
            <Accordion
              expanded={logExpanded}
              onChange={(_event, expanded) => setLogExpanded(expanded)}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid #d0d7de',
                borderRadius: 1,
                '&:before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="terminal-status-log-content"
                id="terminal-status-log-header"
                sx={{
                  px: 2,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  },
                }}
              >
                <Typography variant="subtitle2">Status log</Typography>
                <Chip
                  size="small"
                  label={`${statusLog.length} event${
                    statusLog.length === 1 ? '' : 's'
                  }`}
                  variant="outlined"
                />
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0 }}>
                <Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Location</TableCell>
                        {availableStatusOptions.map((option) => (
                          <TableCell
                            key={option.status}
                            sx={{ minWidth: 180, verticalAlign: 'top' }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Box
                                sx={{ color: option.color, display: 'flex' }}
                              >
                                {createStatusIconElement(
                                  option.icon,
                                  option.status,
                                )}
                              </Box>
                              <Typography variant="body2" fontWeight={600}>
                                {option.label}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locationRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={
                              Math.max(availableStatusOptions.length, 1) + 1
                            }
                          >
                            <Typography variant="body2" color="text.secondary">
                              No status events yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        locationRows.map((location) => {
                          const isSelectedLocation =
                            activeLocation != null &&
                            ((location.legId != null &&
                              activeLocation.legId === location.legId) ||
                              location.locationOrder ===
                                activeLocation.locationOrder);

                          return (
                            <TableRow
                              key={location.locationOrder}
                              selected={isSelectedLocation}
                              sx={{
                                '& .MuiTableCell-root': {
                                  backgroundColor: isSelectedLocation
                                    ? 'rgba(25, 118, 210, 0.08)'
                                    : undefined,
                                },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2">
                                  {getLocationText(
                                    location.locationName,
                                    location.locationCity,
                                  )}
                                </Typography>
                                {isSelectedLocation ? (
                                  <Chip
                                    size="small"
                                    label="Current terminal"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mt: 0.75 }}
                                  />
                                ) : null}
                              </TableCell>
                              {availableStatusOptions.map((option) => {
                                const log = statusLogMatrix.get(
                                  `${location.locationOrder}-${option.status}`,
                                );

                                return (
                                  <TableCell
                                    key={`${location.locationOrder}-${option.status}`}
                                    sx={{
                                      verticalAlign: 'top',
                                    }}
                                  >
                                    {!log ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        —
                                      </Typography>
                                    ) : (
                                      <Box
                                        sx={{
                                          border: `1px solid ${option.color}33`,
                                          backgroundColor: `${option.color}12`,
                                          borderRadius: 1,
                                          px: 1,
                                          py: 0.75,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          display="block"
                                        >
                                          {formatTimestamp(log.timestamp)}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                        >
                                          {log.updatedBy ?? log.createdBy}
                                        </Typography>
                                        {log.locationId === locationId ? (
                                          <Stack
                                            direction="row"
                                            spacing={0.25}
                                            justifyContent="flex-end"
                                            sx={{ mt: 0.5 }}
                                          >
                                            <Tooltip title="Edit">
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleEditLog(log);
                                                  }}
                                                  disabled={
                                                    isDeletingLog ||
                                                    !enabledStatuses.includes(
                                                      log.status,
                                                    )
                                                  }
                                                >
                                                  <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                              <IconButton
                                                size="small"
                                                disabled={isDeletingLog}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  setDeleteLog(log);
                                                }}
                                              >
                                                <DeleteOutlineIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Stack>
                                        ) : null}
                                      </Box>
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteLog != null}
        onClose={() => {
          if (!isDeletingLog) {
            setDeleteLog(null);
          }
        }}
      >
        <DialogTitle>Delete status log entry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isDeletingLog
              ? `Deleting the selected status log entry for ${deleteLogLabel}...`
              : `Delete the selected status log entry for ${deleteLogLabel}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLog(null)} disabled={isDeletingLog}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteLog}
            disabled={isDeletingLog}
          >
            {isDeletingLog ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={gateDialogOpen}
        onClose={handleGateDialogCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Hand over — Placed at (gate / location)</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Handed over at (gate / location)"
            fullWidth
            size="small"
            value={pendingGateNumber}
            onChange={(event) => setPendingGateNumber(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleGateDialogConfirm();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGateDialogCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleGateDialogConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
